import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type Membership = {
  id: string;
  workspace_id: string;
  role: "owner_admin" | "editor" | "viewer";
  status: "invited" | "active" | "revoked";
  accepted_at: string | null;
  invited_at: string;
  workspace_label: string | null;
  workspace_owner_email: string | null;
};

/**
 * Workspaces the current user is an active member of (i.e. they don't own
 * them — they were invited in by someone else). The owned-workspace list
 * lives in `useWorkspace()`.
 */
export function useMyMemberships() {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setMemberships([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const { data: rows, error } = await supabase
      .from("workspace_members")
      .select("id, workspace_id, role, status, accepted_at, invited_at")
      .eq("member_user_id", user.id)
      .eq("status", "active")
      .order("accepted_at", { ascending: false });

    if (error || !rows) {
      setMemberships([]);
      setLoading(false);
      return;
    }

    const ids = rows.map((r) => r.workspace_id);
    const labelMap: Record<string, { label: string | null; owner: string | null }> = {};
    if (ids.length) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, workspace_label, display_name, owner_user_id")
        .in("id", ids);
      for (const p of profiles ?? []) {
        labelMap[p.id] = {
          label: p.workspace_label ?? p.display_name,
          owner: p.owner_user_id,
        };
      }
    }

    setMemberships(
      rows.map((r) => ({
        id: r.id,
        workspace_id: r.workspace_id,
        role: r.role as Membership["role"],
        status: r.status as Membership["status"],
        accepted_at: r.accepted_at,
        invited_at: r.invited_at,
        workspace_label: labelMap[r.workspace_id]?.label ?? null,
        workspace_owner_email: null,
      })),
    );
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { memberships, loading, refresh };
}
